import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AccessControlMonitor = ({ accessLogs, loading, onRefresh, filters, onFilterChange }) => {
  const [viewMode, setViewMode] = useState('logs'); // 'logs' or 'matrix'

  const getActionIcon = (action) => {
    if (action?.includes('login')) return 'LogIn';
    if (action?.includes('logout')) return 'LogOut';
    if (action?.includes('access')) return 'Key';
    if (action?.includes('denied')) return 'ShieldAlert';
    return 'Activity';
  };

  const getActionColor = (action) => {
    if (action?.includes('denied') || action?.includes('failed')) {
      return 'text-red-600 bg-red-50';
    }
    if (action?.includes('success') || action?.includes('granted')) {
      return 'text-green-600 bg-green-50';
    }
    return 'text-blue-600 bg-blue-50';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4]?.map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <Icon name="Lock" className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <span className="break-words">Access Control Monitoring</span>
        </h2>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('logs')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                viewMode === 'logs' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
              }`}
            >
              Access Logs
            </button>
            <button
              onClick={() => setViewMode('matrix')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors whitespace-nowrap ${
                viewMode === 'matrix' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'
              }`}
            >
              Permission Matrix
            </button>
          </div>
          <select
            value={filters?.actionType}
            onChange={(e) => onFilterChange?.({ ...filters, actionType: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-auto min-w-[140px]"
          >
            <option value="all">All Actions</option>
            <option value="login">Login</option>
            <option value="access">Access</option>
            <option value="denied">Denied</option>
            <option value="permission">Permission Change</option>
          </select>
          <Button onClick={onRefresh} variant="outline" size="sm" className="flex-shrink-0">
            <Icon name="RefreshCw" className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {viewMode === 'logs' ? (
        <div className="space-y-3">
          {accessLogs?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="Lock" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-600">No access logs found for the selected filters.</p>
            </div>
          ) : (
            accessLogs?.map(log => (
              <div
                key={log?.id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                  <div className={`p-2 rounded-lg ${getActionColor(log?.action)} flex-shrink-0`}>
                    <Icon name={getActionIcon(log?.action)} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                      <h4 className="font-medium text-gray-900 text-sm sm:text-base break-words">{log?.action}</h4>
                      <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        {new Date(log?.createdAt)?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <Icon name="User" className="w-4 h-4" />
                        {log?.performedBy}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="Database" className="w-4 h-4" />
                        {log?.entityType}
                      </span>
                    </div>
                  </div>
                </div>
                <Button variant="ghost" size="sm">
                  <Icon name="Eye" className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">User Permission Matrix</h3>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Read Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Write Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Delete Access
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin Access
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {[
                  { role: 'Super Admin', read: true, write: true, delete: true, admin: true },
                  { role: 'HR Manager', read: true, write: true, delete: false, admin: false },
                  { role: 'Payroll Officer', read: true, write: true, delete: false, admin: false },
                  { role: 'Department Head', read: true, write: false, delete: false, admin: false },
                  { role: 'Employee', read: true, write: false, delete: false, admin: false }
                ]?.map((permission, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {permission?.role}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission?.read ? (
                        <Icon name="Check" className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon name="X" className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission?.write ? (
                        <Icon name="Check" className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon name="X" className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission?.delete ? (
                        <Icon name="Check" className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon name="X" className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {permission?.admin ? (
                        <Icon name="Check" className="w-5 h-5 text-green-600" />
                      ) : (
                        <Icon name="X" className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessControlMonitor;